import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Check, Trash2, X } from "lucide-react";
import { useLeagueContext } from "@/modules/leagues/hooks/useLeagueContext";
import { useUpdateGeneralSettings } from "@/modules/leagues/hooks/useLeagueAdmin";
import { useDeleteLeague } from "@/modules/leagues/hooks/useLeagues";
import { SettingsSubHeader } from "@/modules/leagues/components/SettingsSubHeader";
import { Input } from "@/components/atoms/Input";
import { Label } from "@/components/atoms/Label";
import { Textarea } from "@/components/atoms/Textarea";
import { Switch } from "@/components/atoms/Switch";
import { Button } from "@/components/atoms/Button";
import { useToast } from "@/hooks/use-toast";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/utils/cn";

/** Compara ignorando maiúscula/minúscula e QUALQUER tipo de espaço
 * (normal, duplo, não-quebrável \u00A0, tab) — motivo real do botão
 * "Excluir de vez" ficando travado mesmo com o nome "certo" digitado:
 * teclado de celular às vezes insere espaço não-quebrável sem o usuário
 * perceber, e uma comparação exata nunca bate nesse caso. */
function normalizeForCompare(value: string): string {
  return value.replace(/\s+/g, " ").trim().toLowerCase();
}

const TOGGLES = [
  { key: "privada" as const, label: "Liga privada", description: "Só entra com código de convite" },
  { key: "permiteDesafios" as const, label: "Permitir Desafios (X1)", description: "Habilita o módulo de desafios entre participantes" },
  { key: "permitePerguntasBonus" as const, label: "Permitir Perguntas Bônus", description: "Admin pode criar perguntas extras por rodada" },
  { key: "permiteImportacaoManual" as const, label: "Permitir Importação Manual", description: "Admin pode ajustar pontos manualmente" },
  { key: "permiteEntradaAposInicio" as const, label: "Permitir entrada após início", description: "Novos participantes podem entrar mesmo com a liga em andamento" },
];

export function LeagueSettingsGeneralPage() {
  const { league } = useLeagueContext();
  const { toast } = useToast();
  const navigate = useNavigate();
  const updateSettings = useUpdateGeneralSettings(league.id);
  const deleteLeague = useDeleteLeague();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const nameMatches = normalizeForCompare(confirmText) === normalizeForCompare(league.nome);
  const canManage = league.myRole === "admin" || league.myRole === "moderator";

  const [form, setForm] = useState({
    nome: league.nome,
    descricao: league.descricao ?? "",
    maxParticipantes: league.maxParticipantes,
    privada: league.privada,
    permiteDesafios: league.permiteDesafios,
    permitePerguntasBonus: league.permitePerguntasBonus,
    permiteImportacaoManual: league.permiteImportacaoManual,
    permiteEntradaAposInicio: league.permiteEntradaAposInicio,
  });

  function handleSave() {
    updateSettings.mutate(
      { ...form, descricao: form.descricao.trim() || null },
      {
        onSuccess: () => toast({ title: "Configurações salvas", description: "As alterações já valem para todos os participantes." }),
        onError: (err) => toast({ title: "Não foi possível salvar", description: err.message, variant: "destructive" }),
      }
    );
  }

  function handleDelete() {
    deleteLeague.mutate(league.id, {
      onSuccess: () => {
        toast({ title: "Liga excluída" });
        navigate(ROUTES.groups);
      },
      onError: (err) =>
        toast({
          title: "Não foi possível excluir a liga",
          description: err instanceof Error ? err.message : "Tente novamente.",
          variant: "destructive",
        }),
    });
  }

  return (
    <div className="space-y-5">
      <SettingsSubHeader leagueId={league.id} title="Configurações Gerais" />
      {!canManage && (
        <p className="-mt-3 text-xs text-muted-foreground">Modo visualização — só o administrador da liga pode alterar essas configurações.</p>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="nome">Nome da liga</Label>
        <Input id="nome" value={form.nome} disabled={!canManage} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="descricao">Descrição</Label>
        <Textarea
          id="descricao"
          value={form.descricao}
          disabled={!canManage}
          onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
          placeholder="Conte do que se trata essa liga..."
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="max">Máximo de participantes</Label>
        <Input
          id="max"
          type="number"
          min={league.participantsCount}
          value={form.maxParticipantes}
          disabled={!canManage}
          onChange={(e) => setForm((f) => ({ ...f, maxParticipantes: Number(e.target.value) }))}
        />
      </div>

      <div className="space-y-2.5">
        {TOGGLES.map((t) => (
          <div key={t.key} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-3 py-3">
            <div>
              <p className="text-sm font-medium">{t.label}</p>
              <p className="text-xs text-muted-foreground">{t.description}</p>
            </div>
            <Switch
              checked={form[t.key]}
              disabled={!canManage}
              onCheckedChange={(checked) => setForm((f) => ({ ...f, [t.key]: checked }))}
            />
          </div>
        ))}
      </div>

      {canManage && (
        <Button className="w-full" isLoading={updateSettings.isPending} onClick={handleSave}>
          Salvar alterações
        </Button>
      )}

      {canManage && (
        <div className="mt-6 space-y-3 rounded-2xl border border-destructive/40 bg-destructive/5 p-4">
          <p className="flex items-center gap-1.5 text-sm font-bold text-destructive">
            <AlertTriangle className="h-4 w-4" /> Zona de risco
          </p>
          <p className="text-xs text-muted-foreground">
            Excluir a liga apaga permanentemente os participantes, palpites, ranking, histórico e duelos dela. Não tem
            como desfazer.
          </p>

          {!confirmDelete ? (
            <Button variant="outline" className="w-full border-destructive text-destructive hover:bg-destructive/10" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="h-4 w-4" /> Excluir liga
            </Button>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="confirm-delete" className="text-xs">
                Digite <strong>{league.nome}</strong> pra confirmar
              </Label>
              <div className="relative">
                <Input
                  id="confirm-delete"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className={cn("pr-9", confirmText.length > 0 && (nameMatches ? "border-success" : "border-destructive"))}
                />
                {confirmText.length > 0 && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2">
                    {nameMatches ? <Check className="h-4 w-4 text-success" /> : <X className="h-4 w-4 text-destructive" />}
                  </span>
                )}
              </div>
              {confirmText.length > 0 && !nameMatches && (
                <p className="text-[11px] text-destructive">Ainda não bateu com o nome exato da liga — confere se não tem espaço ou letra diferente.</p>
              )}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => { setConfirmDelete(false); setConfirmText(""); }}>
                  Cancelar
                </Button>
                <Button
                  className="flex-1 bg-destructive text-destructive-foreground hover:brightness-95"
                  disabled={!nameMatches || deleteLeague.isPending}
                  isLoading={deleteLeague.isPending}
                  onClick={handleDelete}
                >
                  Excluir de vez
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
